import React, { Fragment, ReactNode } from 'react';
import classnames from 'classnames';
import { Dialog, Transition } from '@headlessui/react';
import { RichText } from '../../../hoc/components/rich-text';
import CloseButton from './close-button';

export interface ModalProps {
	open?: boolean;
	title?: string;
	children: ReactNode;
	onClose: Function;
	className?: string;
	contentClassName?: string;
	closable?: boolean;
	scroll?: boolean;
	divRef?: Function;
	onScroll?: () => void;
}

const Modal: React.FC<ModalProps> = ({
	open,
	title,
	children,
	onClose,
	className,
	contentClassName = '',
	closable = true,
	scroll,
	divRef,
	onScroll,
}) => {
	const handleClose = () => {
		if (closable) {
			onClose();
		}
	};

	return (
		<Transition.Root show={open} as={Fragment}>
			<Dialog onClose={handleClose}>
				<div className="fixed z-[100] inset-0 overflow-y-auto test">
					<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0">
							<div className="fixed inset-0 bg-neutral-700 bg-opacity-75 transition-opacity" />
						</Transition.Child>
						{/* This element is to trick the browser into centering the modal contents. */}
						<span
							className="hidden sm:inline-block sm:align-middle sm:h-screen"
							aria-hidden="true">
							&#8203;
						</span>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
							<Dialog.Panel
								className={classnames(
									'relative inline-block sm:align-middle bg-white rounded-lg px-12 pt-24 pb-20 md:pb-24 md:px-28 text-left overflow-hidden shadow-xl transform transition-all max-w-xs w-full md:max-w-2xl sm:my-8',
									{
										'lg:max-w-6xl md:pr-10 pl-6 pr-3': scroll,
									}
								)}>
								<div className="absolute top-0 right-0 pt-8 lg:pr-8 pr-6 print:hidden">
									<CloseButton onClose={onClose} closable={closable} />
								</div>
								<div className="sm:flex sm:items-start">
									<div className={contentClassName}>
										{title && (
											<Dialog.Title
												as="h3"
												className="text-2xl lg:text-3xl font-semibold text-font">
												<RichText html={title} />
											</Dialog.Title>
										)}
										<div
											className={classnames('text-lg text-font', {
												'mt-4': title,
												'overflow-x-hidden max-h-96 pr-2': scroll,
											})}
											ref={(el: HTMLDivElement | null) => {
												if (typeof divRef === 'function') {
													divRef(el);
												}
											}}
											data-testid="modal-div-scrollable"
											onScroll={onScroll}>
											{children}
										</div>
									</div>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	);
};

export default Modal;
